import React from 'react';
import { Shield, Globe, Zap, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-24 px-6 bg-gradient-to-br from-secondary/50 to-background" data-testid="about-hero">
          <div className="container mx-auto max-w-4xl">
            <h1 className="font-heading text-5xl sm:text-6xl font-bold mb-6 tracking-tight">
              Advertising Compliance Is No Longer Optional.
            </h1>
            <p className="text-xl text-muted-foreground">
              ScanMyAd is a RegTech SaaS platform built to protect businesses from the escalating risks of non-compliant advertising in an AI-driven, multi-jurisdiction enforcement environment.
            </p>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-24 px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-heading text-4xl font-bold mb-8">The Compliance Challenge</h2>
            <div className="space-y-6 text-lg text-muted-foreground">
              <p>
                Global regulatory enforcement is accelerating. The FTC in the United States, Competition Bureau in Canada, EU authorities under the Unfair Commercial Practices Directive, and regulatory bodies across India are actively pursuing misleading advertising violations.
              </p>
              <p>
                At the same time, advertising platforms like Meta, Google, TikTok, Amazon, LinkedIn, and YouTube are deploying AI-powered systems to detect policy violations at scale. A single non-compliant ad can result in:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Account suspension or permanent ban</li>
                <li>Loss of advertising privileges across platforms</li>
                <li>Regulatory fines reaching millions of dollars</li>
                <li>Permanent public enforcement records</li>
                <li>Damage to brand reputation and customer trust</li>
              </ul>
              <p>
                For performance marketers, DTC brands, agencies, and advertisers operating in health, finance, wellness, or e-commerce — compliance is business survival.
              </p>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-24 px-6 bg-secondary/30">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-heading text-4xl font-bold mb-8">Our Solution</h2>
            <p className="text-lg text-muted-foreground mb-12">
              ScanMyAd provides AI-powered multi-jurisdiction compliance intelligence. We analyze advertising content BEFORE it goes live, detecting violations across:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Globe className="h-10 w-10 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">Multi-Jurisdiction Analysis</h3>
                  <p className="text-sm text-muted-foreground">
                    Canada (Competition Act, Health Canada, CASL), US (FTC Act, FDA, CAN-SPAM), EU (GDPR, UCPD, AVMSD), India (Consumer Protection Act, ASCI)
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Shield className="h-10 w-10 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">Platform Policy Compliance</h3>
                  <p className="text-sm text-muted-foreground">
                    Meta Ads, Google Ads, TikTok, Amazon, LinkedIn, YouTube — detect violations before submission
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Zap className="h-10 w-10 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">Preventive Intelligence</h3>
                  <p className="text-sm text-muted-foreground">
                    Risk scores, violation detection, suggested rewrites, required disclaimers, and substantiation guidance
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <Users className="h-10 w-10 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">Built for Teams</h3>
                  <p className="text-sm text-muted-foreground">
                    Agency plans with team collaboration, bulk scanning, white-label reports, and API access
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-heading text-4xl font-bold mb-8">Our Commitment</h2>
            <div className="space-y-6 text-lg text-muted-foreground">
              <p>
                ScanMyAd operates under a strict trust framework:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Data Privacy:</strong> Your ad data is encrypted and never resold. PIPEDA and GDPR compliant.</li>
                <li><strong>AI Transparency:</strong> We disclose AI model usage and limitations. No guarantee of regulatory approval.</li>
                <li><strong>Legal Precision:</strong> Our analysis references specific regulations and policies, not generic advice.</li>
                <li><strong>Canadian Foundation:</strong> Registered business in British Columbia, Canada.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Company Info */}
        <section className="py-24 px-6 bg-secondary/30">
          <div className="container mx-auto max-w-4xl text-center">
            <Shield className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold mb-4">Built by Compliance Professionals</h2>
            <p className="text-lg text-muted-foreground mb-6">
              ScanMyAd is developed by regulatory-focused professionals who understand the intersection of advertising, compliance, and technology. Our mission is to reduce legal exposure for businesses operating in complex, multi-jurisdiction markets.
            </p>
            <div className="text-sm text-muted-foreground">
              <p className="font-semibold">BC Leads Business Services</p>
              <p>Unit 108, 13277 108 Avenue</p>
              <p>Surrey, BC V3T 0A9</p>
              <p>British Columbia, Canada</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
